import { useDataLookups } from 'data/data-lookup';
import moment from 'moment';
import Papa from 'papaparse';
import React from 'react';
import { toast } from 'sonner';
import {
  EDataLookupBillingStatus,
  EDataLookupFileKind,
  EDataLookupStatus,
  EDataLookupType,
  EPropertyLookupMode,
  IPropertyDataLookup,
  IPropertyLookupColumnMapping,
} from 'types/data-lookup';

import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  LinearProgress,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloudUploadRoundedIcon from '@material-ui/icons/CloudUploadRounded';
import DescriptionRoundedIcon from '@material-ui/icons/DescriptionRounded';
import GetAppRoundedIcon from '@material-ui/icons/GetAppRounded';
import HistoryRoundedIcon from '@material-ui/icons/HistoryRounded';
import HomeWorkRoundedIcon from '@material-ui/icons/HomeWorkRounded';
import PaymentRoundedIcon from '@material-ui/icons/PaymentRounded';
import PeopleAltRoundedIcon from '@material-ui/icons/PeopleAltRounded';
import SearchRoundedIcon from '@material-ui/icons/SearchRounded';

import InvoicePaymentModal from 'components/Billing/InvoicePaymentModal';
import PaymentMethodSelector from 'components/PaymentMethods/PaymentMethodSelector';

import { downloadDataLookupFile, submitPropertyLookup } from 'utils/api/data-lookups';

const MAX_FILE_SIZE = 15 * 1024 * 1024;
const MAX_INPUT_RECORDS = 2500;
const UNIT_PRICE_CENTS = 15;
const isAuditFile = (kind: EDataLookupFileKind) => kind === EDataLookupFileKind.Audit;

type Mapping = Record<string, string>;

const addressFields = [
  { key: 'address', label: 'Property address', aliases: ['propertyaddress', 'siteaddress', 'streetaddress', 'address'] },
  { key: 'city', label: 'City', aliases: ['propertycity', 'sitecity', 'city'] },
  { key: 'state', label: 'State', aliases: ['propertystate', 'sitestate', 'state'] },
  { key: 'zip', label: 'ZIP code', aliases: ['propertyzip', 'sitezip', 'zipcode', 'zip'] },
];
const parcelFields = [
  { key: 'parcelNumber', label: 'Parcel number', aliases: ['parcelnumber', 'parcelid', 'apn', 'assessorsparcelnumber'] },
  { key: 'zip', label: 'ZIP code', aliases: ['propertyzip', 'sitezip', 'zipcode', 'zip'] },
];
const normalizeHeader = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '');
const money = (cents = 0) => `$${(cents / 100).toFixed(2)}`;

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    width: '100%',
    minWidth: 0,
    gap: theme.spacing(3),
    marginTop: theme.spacing(3),
    paddingBottom: theme.spacing(4),
  },
  card: {
    height: 'auto',
    overflow: 'hidden',
    margin: '0 !important',
    padding: '0 !important',
    border: `1px solid ${theme.palette.grayScale6.main}`,
    borderRadius: 16,
    backgroundColor: theme.palette.white.main,
    boxShadow: '0 10px 30px rgba(23, 23, 24, 0.045)',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(2.25, 3),
    borderBottom: `1px solid ${theme.palette.grayScale6.main}`,
    background: 'linear-gradient(135deg, rgba(150, 214, 179, 0.20) 0%, rgba(246, 246, 247, 0.72) 100%)',
    [theme.breakpoints.down('xs')]: { alignItems: 'flex-start', padding: theme.spacing(2, 2.25) },
  },
  cardHeading: {
    display: 'flex', alignItems: 'center', gap: theme.spacing(1.5), minWidth: 0,
  },
  cardIcon: {
    width: 42,
    height: 42,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: theme.palette.primary.dark,
    backgroundColor: theme.palette.white.main,
    border: '1px solid rgba(86, 145, 127, 0.18)',
    borderRadius: 12,
    boxShadow: '0 4px 12px rgba(60, 85, 71, 0.08)',
    '& svg': { fontSize: 21 },
  },
  cardTitle: {
    color: theme.palette.darkGray.main, fontSize: 17, fontWeight: 700, lineHeight: 1.35,
  },
  cardDescription: {
    color: theme.palette.text.secondary, fontSize: 14, lineHeight: 1.55, marginTop: 3,
  },
  cardBody: {
    padding: theme.spacing(3),
    '&:last-child': { paddingBottom: theme.spacing(3) },
    [theme.breakpoints.down('xs')]: { padding: theme.spacing(2.25), '&:last-child': { paddingBottom: theme.spacing(2.25) } },
  },
  historyCount: {
    flexShrink: 0,
    padding: theme.spacing(0.65, 1.25),
    color: theme.palette.primary.dark,
    backgroundColor: 'rgba(255, 255, 255, 0.76)',
    border: '1px solid rgba(86, 145, 127, 0.18)',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    [theme.breakpoints.down('xs')]: { display: 'none' },
  },
  modePanel: {
    marginBottom: theme.spacing(3),
    paddingBottom: theme.spacing(2.5),
    borderBottom: `1px solid ${theme.palette.grayScale6.main}`,
  },
  modeFieldset: { width: '100%' },
  modeLabel: {
    color: `${theme.palette.darkGray.main} !important`,
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1.4,
    '&.Mui-focused': { color: `${theme.palette.darkGray.main} !important` },
  },
  modeOptions: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(1.5),
    [theme.breakpoints.down('xs')]: { gridTemplateColumns: '1fr' },
  },
  modeOption: {
    minWidth: 0,
    margin: 0,
    padding: theme.spacing(0.75, 1.25),
    border: `1px solid ${theme.palette.grayScale6.main}`,
    borderRadius: 10,
    backgroundColor: theme.palette.white.main,
    cursor: 'pointer',
    transition: 'background-color 160ms ease, border-color 160ms ease, box-shadow 160ms ease',
    '&:hover': {
      borderColor: 'rgba(86, 145, 127, 0.45)',
      backgroundColor: 'rgba(150, 214, 179, 0.08)',
    },
    '&:focus-within': {
      borderColor: theme.palette.primary.dark,
      boxShadow: '0 0 0 3px rgba(86, 145, 127, 0.16)',
    },
    '& .MuiRadio-root': {
      flexShrink: 0,
      color: theme.palette.gray.main,
    },
    '& .MuiRadio-colorPrimary.Mui-checked': { color: theme.palette.primary.dark },
    '& .MuiFormControlLabel-label': {
      overflow: 'hidden',
      color: theme.palette.darkGray.main,
      fontSize: 14,
      fontWeight: 600,
      lineHeight: 1.4,
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
  modeOptionSelected: {
    borderColor: 'rgba(86, 145, 127, 0.48)',
    backgroundColor: 'rgba(150, 214, 179, 0.13)',
    '& .MuiFormControlLabel-label': { color: theme.palette.darkGray.main, fontWeight: 700 },
  },
  uploadZone: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 240,
    padding: theme.spacing(4),
    textAlign: 'center',
    border: '1.5px dashed rgba(86, 145, 127, 0.38)',
    borderRadius: 14,
    background: 'linear-gradient(180deg, rgba(246, 250, 248, 0.72) 0%, rgba(250, 250, 250, 0.95) 100%)',
    transition: 'border-color 160ms ease, background-color 160ms ease, box-shadow 160ms ease',
    [theme.breakpoints.down('xs')]: { minHeight: 220, padding: theme.spacing(3, 2) },
  },
  uploadZoneDragging: {
    borderColor: theme.palette.primary.main,
    background: 'rgba(150, 214, 179, 0.15)',
    boxShadow: 'inset 0 0 0 1px rgba(86, 145, 127, 0.12)',
  },
  uploadIcon: {
    width: 58,
    height: 58,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.dark,
    backgroundColor: theme.palette.white.main,
    border: '1px solid rgba(86, 145, 127, 0.16)',
    borderRadius: 16,
    boxShadow: '0 8px 22px rgba(60, 85, 71, 0.10)',
    '& svg': { fontSize: 28 },
  },
  uploadTitle: { color: theme.palette.darkGray.main, fontSize: 16, fontWeight: 700 },
  uploadHelp: {
    maxWidth: 520, margin: theme.spacing(0.75, 0, 2.25), color: theme.palette.text.secondary, fontSize: 14, lineHeight: 1.6,
  },
  uploadMeta: { marginTop: theme.spacing(1.25), color: theme.palette.text.secondary, fontSize: 13 },
  primaryButton: {
    '&.MuiButton-root': {
      margin: 0,
      padding: '11px 24px',
      borderRadius: 24,
      fontSize: 13,
      fontWeight: 700,
      textTransform: 'none',
      boxShadow: '0 5px 14px rgba(86, 145, 127, 0.18)',
      '& svg': { marginRight: theme.spacing(0.75) },
    },
  },
  fileCard: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 1.75),
    border: `1px solid ${theme.palette.grayScale6.main}`,
    borderRadius: 12,
    backgroundColor: theme.palette.grayScale8.main,
  },
  fileIcon: {
    width: 38,
    height: 38,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: theme.palette.primary.dark,
    backgroundColor: theme.palette.white.main,
    border: `1px solid ${theme.palette.grayScale6.main}`,
    borderRadius: 10,
    '& svg': { fontSize: 20 },
  },
  fileDetails: { minWidth: 0, flex: 1 },
  fileName: {
    overflow: 'hidden', color: theme.palette.darkGray.main, fontSize: 13, fontWeight: 700, textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  fileMeta: {
    color: theme.palette.text.secondary, fontSize: 13, lineHeight: 1.5, marginTop: 3,
  },
  replaceButton: {
    flexShrink: 0, fontSize: 12, fontWeight: 700, textTransform: 'none',
  },
  mappingGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(2.5),
    marginTop: theme.spacing(3),
    [theme.breakpoints.down('sm')]: { gridTemplateColumns: '1fr' },
  },
  mappingSection: {
    minWidth: 0, padding: theme.spacing(2.25), border: `1px solid ${theme.palette.grayScale6.main}`, borderRadius: 14, backgroundColor: theme.palette.white.main,
  },
  mappingSectionHeader: {
    display: 'flex', alignItems: 'flex-start', gap: theme.spacing(1.25), marginBottom: theme.spacing(3),
  },
  mappingSectionIcon: {
    width: 34,
    height: 34,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: theme.palette.primary.dark,
    backgroundColor: 'rgba(150, 214, 179, 0.16)',
    borderRadius: 9,
    '& svg': { fontSize: 18 },
  },
  mappingSectionTitle: { color: theme.palette.darkGray.main, fontSize: 14, fontWeight: 700 },
  mappingSectionDescription: {
    color: theme.palette.text.secondary, fontSize: 13, lineHeight: 1.55, marginTop: 3,
  },
  fieldsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    columnGap: theme.spacing(2.5),
    rowGap: theme.spacing(3),
    [theme.breakpoints.down('xs')]: { gridTemplateColumns: '1fr', rowGap: theme.spacing(2.5) },
  },
  fieldControl: { minWidth: 0, '& .MuiOutlinedInput-root': { backgroundColor: theme.palette.grayScale8.main } },
  paymentControl: { minWidth: 0, '& > div': { margin: '0 !important' } },
  estimate: {
    marginTop: theme.spacing(2.5),
    padding: theme.spacing(1.75),
    border: '1px solid rgba(86, 145, 127, 0.18)',
    borderRadius: 12,
    backgroundColor: 'rgba(150, 214, 179, 0.12)',
  },
  estimateEyebrow: {
    color: theme.palette.primary.dark, fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
  },
  estimateValue: {
    margin: theme.spacing(0.5, 0), color: theme.palette.darkGray.main, fontSize: 20, fontWeight: 700,
  },
  estimateHelp: { color: theme.palette.text.secondary, fontSize: 13, lineHeight: 1.65 },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2.5),
    marginTop: theme.spacing(3),
    paddingTop: theme.spacing(2.5),
    borderTop: `1px solid ${theme.palette.grayScale6.main}`,
    [theme.breakpoints.down('xs')]: { alignItems: 'stretch', flexDirection: 'column' },
  },
  actionButtons: {
    display: 'flex', alignItems: 'center', gap: theme.spacing(1), [theme.breakpoints.down('xs')]: { '& > button, & > label': { flex: 1 } },
  },
  clearButton: {
    '&.MuiButton-root:hover': { backgroundColor: 'transparent' },
    '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.primary.dark}`, outlineOffset: 2 },
  },
  uploadProgress: {
    flex: 1, maxWidth: 360, padding: theme.spacing(1.25, 1.5), borderRadius: 10, backgroundColor: theme.palette.grayScale8.main, [theme.breakpoints.down('xs')]: { maxWidth: 'none' },
  },
  progressLabel: {
    display: 'flex', justifyContent: 'space-between', marginBottom: theme.spacing(0.75), color: theme.palette.text.secondary, fontSize: 11, fontWeight: 600,
  },
  table: { minWidth: 1180 },
  tableContainer: {
    overflowX: 'auto',
    boxShadow: 'none',
    '& .MuiTableCell-head': {
      color: theme.palette.text.secondary,
      backgroundColor: theme.palette.grayScale8.main,
      borderBottom: `1px solid ${theme.palette.grayScale6.main}`,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
    },
    '& .MuiTableCell-body': {
      color: theme.palette.darkGray.main, borderBottom: `1px solid ${theme.palette.grayScale6.main}`, fontSize: 12, verticalAlign: 'top',
    },
    '& .MuiTableRow-root:last-child .MuiTableCell-body': { borderBottom: 0 },
    '& .MuiTableRow-root:hover .MuiTableCell-body': { backgroundColor: 'rgba(246, 246, 247, 0.45)' },
  },
  historyBody: { padding: '0 !important' },
  inputFileCell: {
    display: 'flex', alignItems: 'center', gap: theme.spacing(1), minWidth: 190, fontWeight: 600, '& svg': { color: theme.palette.primary.dark, fontSize: 18, flexShrink: 0 },
  },
  detailCell: { minWidth: 135 },
  rowProgress: { marginTop: theme.spacing(0.75) },
  secondaryText: {
    display: 'block', marginTop: 3, color: theme.palette.text.secondary, fontSize: 11, lineHeight: 1.5,
  },
  costSummary: {
    display: 'flex', alignItems: 'baseline', gap: theme.spacing(0.5), flexWrap: 'wrap',
  },
  costPropertyCount: { color: theme.palette.text.secondary, fontSize: 11, lineHeight: 1.5 },
  billingActions: {
    display: 'flex', alignItems: 'center', gap: theme.spacing(1), marginTop: theme.spacing(1),
  },
  invoiceLink: {
    '&.MuiButton-root': {
      margin: 0, padding: 0, minWidth: 0, color: theme.palette.primary.dark, fontSize: 11, fontWeight: 700, textTransform: 'none',
    },
    '&.MuiButton-root:hover': { color: theme.palette.orange.main, backgroundColor: 'transparent' },
  },
  downloadActions: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    minWidth: 150,
    '& .MuiButton-root': {
      borderRadius: 8, fontSize: 11, fontWeight: 700, textTransform: 'none',
    },
  },
  statusChip: { border: '1px solid currentColor', fontWeight: 700 },
  requestedChip: { backgroundColor: '#fff7ed', color: '#9a3412', borderColor: '#fed7aa' },
  processingChip: { backgroundColor: '#eff6ff', color: '#1d4ed8', borderColor: '#bfdbfe' },
  completedChip: { backgroundColor: '#ecfdf5', color: '#047857', borderColor: '#a7f3d0' },
  failedChip: { backgroundColor: '#fef2f2', color: '#b91c1c', borderColor: '#fecaca' },
  paidChip: { backgroundColor: '#ecfdf5', color: '#047857', borderColor: '#a7f3d0' },
  unpaidChip: { backgroundColor: '#FFF3E0', color: '#A44200', borderColor: '#A44200' },
  payInvoiceButton: {
    '&.MuiButton-root': { color: '#A44200', borderColor: '#A44200' },
    '&.MuiButton-root:hover': { color: '#A44200', borderColor: '#A44200', backgroundColor: '#FFF3E0' },
  },
  emptyState: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', padding: theme.spacing(6, 3), textAlign: 'center',
  },
  emptyIcon: {
    width: 52, height: 52, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: theme.spacing(1.5), color: theme.palette.primary.dark, backgroundColor: 'rgba(150, 214, 179, 0.16)', borderRadius: 14, '& svg': { fontSize: 25 },
  },
  emptyTitle: { color: theme.palette.darkGray.main, fontSize: 14, fontWeight: 700 },
  emptyDescription: {
    maxWidth: 390, marginTop: theme.spacing(0.75), color: theme.palette.text.secondary, fontSize: 14, lineHeight: 1.6,
  },
  pagination: { borderTop: `1px solid ${theme.palette.grayScale6.main}` },
}));

type LookupCardHeaderProps = {
  icon: React.ReactNode;
  title: string;
  description: string;
  action?: React.ReactNode;
};

const LookupCardHeader: React.FC<LookupCardHeaderProps> = ({
  icon, title, description, action,
}) => {
  const classes = useStyles();
  return (
    <div className={classes.cardHeader}>
      <div className={classes.cardHeading}>
        <span className={classes.cardIcon} aria-hidden="true">{icon}</span>
        <div>
          <Typography component="h2" className={classes.cardTitle}>{title}</Typography>
          <Typography component="p" className={classes.cardDescription}>{description}</Typography>
        </div>
      </div>
      {action}
    </div>
  );
};

const statusLabels: Record<EDataLookupStatus, string> = {
  [EDataLookupStatus.Requested]: 'Requested',
  [EDataLookupStatus.Processing]: 'Processing',
  [EDataLookupStatus.Completed]: 'Completed',
  [EDataLookupStatus.Failed]: 'Failed',
};

const PropertyLookupPage: React.FC<{ readOnly?: boolean }> = ({ readOnly = false }) => {
  const classes = useStyles();
  const [mode, setMode] = React.useState(EPropertyLookupMode.Address);
  const [file, setFile] = React.useState<File>();
  const [headers, setHeaders] = React.useState<string[]>([]);
  const [rowCount, setRowCount] = React.useState(0);
  const [mapping, setMapping] = React.useState<Mapping>({});
  const [paymentMethodId, setPaymentMethodId] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isDragging, setIsDragging] = React.useState(false);
  const [uploadProgress, setUploadProgress] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [invoiceToPay, setInvoiceToPay] = React.useState<IPropertyDataLookup>();
  const {
    dataLookups, pagination, isLoading, mutate,
  } = useDataLookups(page + 1, rowsPerPage, EDataLookupType.PropertyLookup);
  const propertyLookups = (dataLookups ?? []) as IPropertyDataLookup[];
  const fields = mode === EPropertyLookupMode.Address ? addressFields : parcelFields;
  const estimate = rowCount * UNIT_PRICE_CENTS;

  const suggestMapping = React.useCallback((nextHeaders: string[], nextMode: EPropertyLookupMode) => {
    const candidates = nextHeaders.map((header) => ({ header, normalized: normalizeHeader(header) }));
    const nextFields = nextMode === EPropertyLookupMode.Address ? addressFields : parcelFields;
    return Object.fromEntries(nextFields.map((field) => {
      const match = candidates.find(({ normalized }) => field.aliases.includes(normalized))
        ?? candidates.find(({ normalized }) => field.aliases.some((alias) => normalized.includes(alias)));
      return [field.key, match?.header ?? ''];
    }));
  }, []);

  const resetFile = () => {
    setFile(undefined);
    setHeaders([]);
    setRowCount(0);
    setMapping({});
    setUploadProgress(0);
  };

  const selectFile = (selected?: File) => {
    if (!selected) return;
    if (!selected.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please choose a CSV file.');
      return;
    }
    if (selected.size > MAX_FILE_SIZE) {
      toast.error('CSV files must be 15 MB or smaller.');
      return;
    }
    Papa.parse<Record<string, string>>(selected, {
      header: true,
      preview: MAX_INPUT_RECORDS + 1,
      skipEmptyLines: true,
      complete: (results) => {
        const nextHeaders = results.meta.fields ?? [];
        if (!nextHeaders.length || !results.data.length) {
          toast.error('The CSV must contain headers and at least one data row.');
          return;
        }
        if (results.data.length > MAX_INPUT_RECORDS) {
          toast.error(`CSV files can contain up to ${MAX_INPUT_RECORDS.toLocaleString()} records.`);
          return;
        }
        setFile(selected);
        setHeaders(nextHeaders);
        setRowCount(results.data.length);
        const parcelMapping = suggestMapping(nextHeaders, EPropertyLookupMode.ParcelNumber);
        const hasParcelColumns = parcelFields.every(({ key }) => !!parcelMapping[key]);
        const suggestedMode = hasParcelColumns
          ? EPropertyLookupMode.ParcelNumber
          : EPropertyLookupMode.Address;
        setMode(suggestedMode);
        setMapping(hasParcelColumns
          ? parcelMapping
          : suggestMapping(nextHeaders, EPropertyLookupMode.Address));
      },
      error: () => toast.error('The CSV could not be read.'),
    });
  };

  const onFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    selectFile(input.files?.[0]);
    input.value = '';
  };

  const onFileDropped = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    if (!isSubmitting) selectFile(event.dataTransfer.files?.[0]);
  };

  const changeMode = (nextMode: EPropertyLookupMode) => {
    setMode(nextMode);
    setMapping(suggestMapping(headers, nextMode));
  };
  const canSubmit = !!file && !!paymentMethodId && fields.every(({ key }) => mapping[key]);

  const submit = async () => {
    if (!file || !canSubmit) return;
    setIsSubmitting(true);
    setUploadProgress(0);
    try {
      await submitPropertyLookup(file, mode, mapping as IPropertyLookupColumnMapping, paymentMethodId, setUploadProgress);
      toast.success('Property lookup started. It will continue in the background.');
      resetFile();
      setPage(0);
      await mutate();
    } catch (error: any) {
      toast.error(error?.response?.data?.error ?? 'The property lookup could not be submitted.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const download = async (lookup: IPropertyDataLookup, outputFile: IPropertyDataLookup['outputFiles'][number]) => {
    try {
      await downloadDataLookupFile(lookup._id, outputFile);
    } catch {
      toast.error('The result could not be downloaded.');
    }
  };

  const statusClass = (status: EDataLookupStatus) => ({
    [EDataLookupStatus.Requested]: classes.requestedChip,
    [EDataLookupStatus.Processing]: classes.processingChip,
    [EDataLookupStatus.Completed]: classes.completedChip,
    [EDataLookupStatus.Failed]: classes.failedChip,
  }[status]);

  return (
    <div className={classes.root}>
      {!readOnly && (
        <Card variant="outlined" className={classes.card}>
          <LookupCardHeader
            icon={<PeopleAltRoundedIcon />}
            title="Create a Property Lookup"
            description="Find owner phone numbers, email addresses, and complete property details"
          />
          <CardContent className={classes.cardBody}>
            <form onSubmit={(event) => { event.preventDefault(); submit(); }}>
              {!file ? (
                <div
                  className={`${classes.uploadZone} ${isDragging ? classes.uploadZoneDragging : ''}`}
                  onDragEnter={(event) => { event.preventDefault(); setIsDragging(true); }}
                  onDragOver={(event) => event.preventDefault()}
                  onDragLeave={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget as Node)) setIsDragging(false);
                  }}
                  onDrop={onFileDropped}
                >
                  <span className={classes.uploadIcon} aria-hidden="true"><CloudUploadRoundedIcon /></span>
                  <Typography component="h3" className={classes.uploadTitle}>Upload your property CSV</Typography>
                  <Typography component="p" className={classes.uploadHelp}>
                    Drag and drop a file here, or choose one from your computer. You’ll map the required columns before the lookup begins.
                  </Typography>
                  <Button className={classes.primaryButton} color="primary" variant="contained" component="label" disabled={isSubmitting}>
                    <CloudUploadRoundedIcon />
                    Choose CSV file
                    <input type="file" accept=".csv,text/csv" hidden onChange={onFileSelected} />
                  </Button>
                  <Typography component="p" className={classes.uploadMeta}>CSV format · Up to 2,500 rows or 15 MB</Typography>
                </div>
              ) : (
                <>
                  <div className={classes.fileCard}>
                    <span className={classes.fileIcon} aria-hidden="true"><DescriptionRoundedIcon /></span>
                    <div className={classes.fileDetails}>
                      <div className={classes.fileName}>{file.name}</div>
                      <div className={classes.fileMeta}>
                        {`${(file.size / 1024).toFixed(1)} KB · ${rowCount.toLocaleString()} records · ${headers.length} columns detected`}
                      </div>
                    </div>
                    <Button className={classes.replaceButton} color="primary" component="label" disabled={isSubmitting}>
                      Replace file
                      <input type="file" accept=".csv,text/csv" hidden onChange={onFileSelected} />
                    </Button>
                  </div>

                  <div className={classes.mappingGrid}>
                    <section className={classes.mappingSection} aria-labelledby="property-lookup-fields-heading">
                      <div className={classes.mappingSectionHeader}>
                        <span className={classes.mappingSectionIcon} aria-hidden="true"><HomeWorkRoundedIcon /></span>
                        <div>
                          <Typography id="property-lookup-fields-heading" component="h3" className={classes.mappingSectionTitle}>
                            Column Mapping
                          </Typography>
                          <Typography component="p" className={classes.mappingSectionDescription}>
                            Choose how to match each property, then map the required CSV columns.
                          </Typography>
                        </div>
                      </div>
                      <div className={classes.modePanel}>
                        <FormControl component="fieldset" className={classes.modeFieldset}>
                          <FormLabel component="legend" className={classes.modeLabel}>How should we match your properties?</FormLabel>
                          <RadioGroup
                            row
                            className={classes.modeOptions}
                            value={mode}
                            onChange={(event) => changeMode(event.target.value as EPropertyLookupMode)}
                          >
                            <FormControlLabel
                              className={`${classes.modeOption} ${mode === EPropertyLookupMode.Address ? classes.modeOptionSelected : ''}`}
                              value={EPropertyLookupMode.Address}
                              control={<Radio color="primary" />}
                              label="Property address"
                              disabled={isSubmitting}
                            />
                            <FormControlLabel
                              className={`${classes.modeOption} ${mode === EPropertyLookupMode.ParcelNumber ? classes.modeOptionSelected : ''}`}
                              value={EPropertyLookupMode.ParcelNumber}
                              control={<Radio color="primary" />}
                              label="Parcel number + ZIP"
                              disabled={isSubmitting}
                            />
                          </RadioGroup>
                        </FormControl>
                      </div>
                      <div className={classes.fieldsGrid}>
                        {fields.map(({ key, label }) => (
                          <FormControl className={classes.fieldControl} variant="outlined" size="small" required key={key}>
                            <InputLabel id={`${key}-mapping-label`}>{label}</InputLabel>
                            <Select
                              labelId={`${key}-mapping-label`}
                              id={`${key}-mapping`}
                              value={mapping[key] ?? ''}
                              label={label}
                              onChange={(event) => setMapping({ ...mapping, [key]: event.target.value as string })}
                            >
                              <MenuItem value=""><em>Select a CSV column</em></MenuItem>
                              {headers.map((header) => <MenuItem value={header} key={header}>{header}</MenuItem>)}
                            </Select>
                          </FormControl>
                        ))}
                      </div>
                    </section>

                    <section className={classes.mappingSection} aria-labelledby="property-lookup-payment-heading">
                      <div className={classes.mappingSectionHeader}>
                        <span className={classes.mappingSectionIcon} aria-hidden="true"><PaymentRoundedIcon /></span>
                        <div>
                          <Typography id="property-lookup-payment-heading" component="h3" className={classes.mappingSectionTitle}>Payment and estimate</Typography>
                          <Typography component="p" className={classes.mappingSectionDescription}>
                            You’ll only pay for unique matched properties returned.
                          </Typography>
                        </div>
                      </div>
                      <div className={classes.paymentControl}>
                        <PaymentMethodSelector
                          labelText="Payment method"
                          inputProps={{
                            id: 'property-lookup-payment-method',
                            value: paymentMethodId,
                            disabled: isSubmitting,
                            onChange: (event: React.ChangeEvent<{ value: unknown }>) => setPaymentMethodId(event.target.value as string),
                          }}
                        />
                      </div>
                      <div className={classes.estimate}>
                        <Typography component="p" className={classes.estimateEyebrow}>$0.15 per matched property</Typography>
                        <Typography component="p" className={classes.estimateValue}>{`Estimated maximum: ${money(estimate)}`}</Typography>
                        <Typography component="p" className={classes.estimateHelp}>
                          {`Estimated ${rowCount.toLocaleString()} ${rowCount === 1 ? 'record' : 'records'}. Unmatched and duplicate properties are not charged; your final invoice uses only unique properties returned.`}
                        </Typography>
                      </div>
                    </section>
                  </div>

                  <div className={classes.actions}>
                    <div className={classes.actionButtons}>
                      <Button className={classes.primaryButton} color="primary" variant="contained" type="submit" disabled={!canSubmit || isSubmitting}>
                        {isSubmitting ? 'Submitting…' : 'Start Property Lookup'}
                      </Button>
                      <Button className={classes.clearButton} disabled={isSubmitting} onClick={resetFile}>Clear</Button>
                    </div>
                    {isSubmitting && (
                      <div className={classes.uploadProgress} aria-live="polite">
                        <div className={classes.progressLabel}>
                          <span>Uploading CSV</span>
                          <span>{`${uploadProgress}%`}</span>
                        </div>
                        <LinearProgress variant="determinate" value={uploadProgress} />
                      </div>
                    )}
                  </div>
                </>
              )}
            </form>
          </CardContent>
        </Card>
      )}

      {(readOnly || isLoading || propertyLookups.length > 0) && (
        <Card variant="outlined" className={classes.card}>
          <LookupCardHeader
            icon={<HistoryRoundedIcon />}
            title={readOnly ? 'Property Lookups' : 'Lookup History'}
            description={readOnly
              ? 'All agent property lookups'
              : 'Jobs continue in the background and active results update automatically'}
            action={pagination?.total ? <span className={classes.historyCount}>{`${pagination.total.toLocaleString()} total`}</span> : undefined}
          />
          {isLoading ? (
            <CardContent className={classes.cardBody}>
              <Box display="flex" justifyContent="center" p={4}><CircularProgress aria-label="Loading lookup history" /></Box>
            </CardContent>
          ) : propertyLookups.length ? (
            <CardContent className={classes.historyBody}>
              <TableContainer className={classes.tableContainer}>
                <Table className={classes.table} aria-label="Property lookup history">
                  <TableHead>
                    <TableRow>
                      <TableCell>Input file</TableCell>
                      {readOnly && <TableCell>Submitted by</TableCell>}
                      <TableCell>Submitted</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Progress / Results</TableCell>
                      <TableCell>Cost</TableCell>
                      <TableCell>Downloads</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {propertyLookups.map((lookup) => {
                      const isCompleted = lookup.status === EDataLookupStatus.Completed;
                      const paid = [EDataLookupBillingStatus.Paid, EDataLookupBillingStatus.NotCharged].includes(lookup.billing.billingStatus);
                      const invoicePaid = lookup.billing.billingStatus === EDataLookupBillingStatus.Paid;
                      const notCharged = lookup.billing.billingStatus === EDataLookupBillingStatus.NotCharged;
                      const paymentFailed = lookup.billing.billingStatus === EDataLookupBillingStatus.PaymentFailed;
                      const propertyCount = lookup.billing.billableRecordCount ?? lookup.outputRecordCount;
                      const { invoice } = lookup.billing;
                      const progress = lookup.inputRecordCount
                        ? Math.round((lookup.processedRecordCount / lookup.inputRecordCount) * 100)
                        : 0;
                      return (
                        <TableRow key={lookup._id}>
                          <TableCell>
                            <span className={classes.inputFileCell}>
                              <DescriptionRoundedIcon aria-hidden="true" />
                              {lookup.inputFiles[0]?.filename ?? 'CSV upload'}
                            </span>
                            <Typography component="span" className={classes.secondaryText}>
                              {lookup.configuration.lookupMode === EPropertyLookupMode.Address ? 'Address lookup' : 'Parcel + ZIP lookup'}
                            </Typography>
                          </TableCell>
                          {readOnly && (
                            <TableCell className={classes.detailCell}>
                              <Typography variant="body2">
                                {[lookup.submittedBy.firstName, lookup.submittedBy.lastName].filter(Boolean).join(' ')
                                || lookup.submittedBy.email
                                || 'Unknown user'}
                              </Typography>
                              {(lookup.submittedBy.firstName || lookup.submittedBy.lastName) && lookup.submittedBy.email && (
                                <Typography component="span" className={classes.secondaryText}>{lookup.submittedBy.email}</Typography>
                              )}
                            </TableCell>
                          )}
                          <TableCell className={classes.detailCell}>{moment(lookup.createdAt).format('MMM D, YYYY h:mm A')}</TableCell>
                          <TableCell>
                            <Chip size="small" label={statusLabels[lookup.status]} className={`${classes.statusChip} ${statusClass(lookup.status)}`} />
                          </TableCell>
                          <TableCell className={classes.detailCell}>
                            {isCompleted ? (
                              <>
                                <Typography variant="body2">{`${lookup.outputRecordCount.toLocaleString()} matched`}</Typography>
                                <Typography component="span" className={classes.secondaryText}>
                                  {`${lookup.failedRecordCount.toLocaleString()} unmatched · ${lookup.duplicateRecordCount.toLocaleString()} duplicate`}
                                </Typography>
                              </>
                            ) : (
                              <>
                                <Typography variant="body2">
                                  {lookup.status === EDataLookupStatus.Failed
                                    ? 'Lookup failed'
                                    : `${lookup.processedRecordCount.toLocaleString()} / ${lookup.inputRecordCount.toLocaleString()} records`}
                                </Typography>
                                {lookup.status === EDataLookupStatus.Failed && (
                                  <Typography component="span" className={classes.secondaryText}>
                                    {`${lookup.processedRecordCount.toLocaleString()} / ${lookup.inputRecordCount.toLocaleString()} processed`}
                                  </Typography>
                                )}
                                {lookup.status === EDataLookupStatus.Processing && (
                                  <LinearProgress className={classes.rowProgress} variant="determinate" value={progress} />
                                )}
                              </>
                            )}
                          </TableCell>
                          <TableCell className={classes.detailCell}>
                            {invoicePaid || notCharged || paymentFailed ? (
                              <>
                                <div className={classes.costSummary}>
                                  <Typography component="span" variant="body2">{money(lookup.billing.actualAmount)}</Typography>
                                  <Typography component="span" className={classes.costPropertyCount}>
                                    {`- ${propertyCount.toLocaleString()} ${propertyCount === 1 ? 'property' : 'properties'}`}
                                  </Typography>
                                </div>
                                <div className={classes.billingActions}>
                                  <Chip
                                    size="small"
                                    label={paymentFailed ? 'Unpaid' : invoicePaid ? 'Paid' : 'Not charged'}
                                    className={`${classes.statusChip} ${paymentFailed ? classes.unpaidChip : classes.paidChip}`}
                                  />
                                  {invoicePaid && invoice?.hostedInvoiceUrl && (
                                    <Button className={classes.invoiceLink} href={invoice.hostedInvoiceUrl} target="_blank" rel="noopener noreferrer">View invoice</Button>
                                  )}
                                </div>
                              </>
                            ) : (
                              <Typography variant="body2">{`Estimated ${money(lookup.billing.estimatedAmount)}`}</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {paid ? (
                              <div className={classes.downloadActions}>
                                {[...lookup.outputFiles]
                                  .sort((left, right) => Number(isAuditFile(left.kind)) - Number(isAuditFile(right.kind)))
                                  .map((outputFile) => (
                                    <Button
                                      key={outputFile._id}
                                      size="small"
                                      variant="outlined"
                                      startIcon={<GetAppRoundedIcon />}
                                      onClick={() => download(lookup, outputFile)}
                                      aria-label={`Download ${isAuditFile(outputFile.kind) ? 'audit' : 'results'} CSV`}
                                    >
                                      {isAuditFile(outputFile.kind) ? 'Audit' : 'Results'}
                                    </Button>
                                  ))}
                              </div>
                            ) : !readOnly && paymentFailed && invoice ? (
                              <div className={classes.downloadActions}>
                                <Button
                                  className={classes.payInvoiceButton}
                                  variant="outlined"
                                  startIcon={<PaymentRoundedIcon />}
                                  onClick={() => setInvoiceToPay(lookup)}
                                >
                                  Pay invoice
                                </Button>
                              </div>
                            ) : lookup.status === EDataLookupStatus.Failed ? (
                              <Typography component="span" className={classes.secondaryText}>
                                Lookup failed
                              </Typography>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                className={classes.pagination}
                component="div"
                count={pagination?.total ?? 0}
                page={page}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[10, 25, 50]}
                onChangePage={(_, nextPage) => setPage(nextPage)}
                onPageChange={(_, nextPage) => setPage(nextPage)}
                onChangeRowsPerPage={(event) => {
                  setRowsPerPage(Number.parseInt(event.target.value, 10));
                  setPage(0);
                }}
              />
            </CardContent>
          ) : (
            <CardContent className={classes.emptyState}>
              <span className={classes.emptyIcon} aria-hidden="true"><SearchRoundedIcon /></span>
              <Typography component="h3" className={classes.emptyTitle}>No property lookups yet</Typography>
              <Typography component="p" className={classes.emptyDescription}>
                {readOnly
                  ? 'No property lookups have been submitted across the platform yet.'
                  : 'Upload a property CSV to find owner phone numbers, email addresses, and complete property details.'}
              </Typography>
            </CardContent>
          )}
        </Card>
      )}

      <InvoicePaymentModal
        open={!!invoiceToPay}
        onClose={() => setInvoiceToPay(undefined)}
        onPaid={async () => { await mutate(); }}
        invoice={invoiceToPay?.billing.invoice ? {
          id: invoiceToPay.billing.invoice.id,
          number: invoiceToPay.billing.invoice.number,
          created: moment(invoiceToPay.billing.invoice.createdAt).unix(),
        } : undefined}
      />
    </div>
  );
};

export default PropertyLookupPage;
